import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

import { ConfluenceEdition, useSessionData } from '@confluence/session-data';
import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';

type improveAuaConversionAnalyticAttributes = {
	touchpointId: string;
	userCount: number;
	dataUsed: number;
	isSiteAdmin?: boolean;
	isBillingAdmin?: boolean;
};

export type improveAuaConversionExperimentProps = {
	improveAuaConversionAnalyticAttributes?: improveAuaConversionAnalyticAttributes;
	skip: boolean;
};

type AuaExperimentCohort = 'control' | 'not-enrolled' | 'treatment';

type UseImproveAuaConversionExperimentProps = {
	improveAuaConversionAnalyticAttributes?: improveAuaConversionAnalyticAttributes;
	skip: boolean;
	isExperimentEnabled: boolean;
	experimentName: string;
	cohort: AuaExperimentCohort;
	fireExposure: () => void;
};

type ExperimentResult = {
	cohort: AuaExperimentCohort;
	renderTreatment: boolean;
	isExperimentEnabled: boolean;
	isEligible: boolean;
};

const useImproveAuaConversionExperiment = ({
	improveAuaConversionAnalyticAttributes,
	skip,
	cohort,
	experimentName,
	fireExposure,
	isExperimentEnabled,
}: UseImproveAuaConversionExperimentProps): ExperimentResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition } = useSessionData();
	const isFreeEdition = edition === ConfluenceEdition.FREE;
	useEffect(() => {
		if (!isExperimentEnabled || skip || !cohort || cohort === 'not-enrolled' || !isFreeEdition) {
			return;
		}

		fireExposure();
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'exposed',
				actionSubject: 'experiment',
				actionSubjectId: `${experimentName}-exposed`,
				attributes: {
					cohort,
					...improveAuaConversionAnalyticAttributes,
				},
				source: experimentName,
			},
		}).fire();
	}, [
		cohort,
		createAnalyticsEvent,
		improveAuaConversionAnalyticAttributes,
		skip,
		experimentName,
		fireExposure,
		isFreeEdition,
		isExperimentEnabled,
	]);

	return {
		cohort,
		isExperimentEnabled,
		isEligible: isExperimentEnabled && isFreeEdition,
		renderTreatment: isExperimentEnabled && !skip && isFreeEdition && cohort === 'treatment',
	};
};

export const useImproveAuaConversionABExperiment = ({
	improveAuaConversionAnalyticAttributes,
	skip,
}: improveAuaConversionExperimentProps) => {
	const [experimentConfig, fireExposure] = UNSAFE_noExposureExp(
		'confluence_auto_upgrade_awareness_v2',
	);
	const cohort = experimentConfig.get('cohort', 'not-enrolled') as AuaExperimentCohort;
	const abExperimentIsEnabled = Boolean(fg('confluence_auto_upgrade_awareness_v2_fg'));

	return useImproveAuaConversionExperiment({
		cohort,
		fireExposure,
		improveAuaConversionAnalyticAttributes,
		skip,
		isExperimentEnabled: abExperimentIsEnabled,
		experimentName: 'confluence_auto_upgrade_awareness_v2',
	});
};

export const useImproveAuaConversionAAExperiment = ({
	improveAuaConversionAnalyticAttributes,
	skip,
}: improveAuaConversionExperimentProps) => {
	const [experimentConfig, fireExposure] = UNSAFE_noExposureExp(
		'confluence_auto_upgrade_awareness_v2_aa',
	);
	const cohort = experimentConfig.get('cohort', 'not-enrolled') as AuaExperimentCohort;

	const aaExperimentIsEnabled = Boolean(fg('confluence_auto_upgrade_awareness_v2_aa_fg'));

	return useImproveAuaConversionExperiment({
		improveAuaConversionAnalyticAttributes,
		cohort,
		fireExposure,
		skip,
		experimentName: 'confluence_auto_upgrade_awareness_v2_aa',
		isExperimentEnabled: aaExperimentIsEnabled,
	});
};
